/**
 * Charges fixes : répartition sur la période, et écritures.
 *
 * Les écritures invalident TOUTES les entrées `charges-stats` : le Résumé lit
 * la même requête, et une charge saisie doit y peser sans rechargement.
 */

import type { ChargesStats, FixedCharge, FixedChargeInput } from '@shared/types/charges';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, createCharge, deleteCharge, fetchChargesStats, updateCharge } from '../lib/api';
import { useAuth } from './useAuth';
import { useSectionStats, type SectionStatsOptions } from './useSectionStats';

const CHARGES_KEY = 'charges-stats';

export function useChargesStats(options: SectionStatsOptions) {
  return useSectionStats<ChargesStats>(CHARGES_KEY, fetchChargesStats, options);
}

export function useChargeMutations() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const requireToken = (): string => {
    if (!token) throw new ApiRequestError(401, 'Session expiree');
    return token;
  };
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: [CHARGES_KEY] });
  };

  const create = useMutation<FixedCharge, Error, FixedChargeInput>({
    mutationFn: async (input) => (await createCharge(input, requireToken())).charge,
    onSuccess: invalidate,
  });
  const update = useMutation<FixedCharge, Error, { id: number; input: FixedChargeInput }>({
    mutationFn: async ({ id, input }) => (await updateCharge(id, input, requireToken())).charge,
    onSuccess: invalidate,
  });
  const remove = useMutation<void, Error, number>({
    mutationFn: (id) => deleteCharge(id, requireToken()),
    onSuccess: invalidate,
  });

  return {
    create: (input: FixedChargeInput) => create.mutateAsync(input),
    update: (id: number, input: FixedChargeInput) => update.mutateAsync({ id, input }),
    remove: (id: number) => remove.mutateAsync(id),
    isSaving: create.isPending || update.isPending || remove.isPending,
  };
}
